import React, { useEffect, useRef, useState } from 'react';
import { navigate, safeFetch } from '../../App';
import { Search, User, Menu, Info, MessageCircle, Share2, X, LogOut, LogIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconButton } from '../ui/IconButton';
import { ShareMenu } from '../ui/ShareMenu';
import clsx from 'clsx';

/**
 * Top app bar.
 *
 * - One row, sticky, soft-shadowed cream surface
 * - Three icon-buttons (search, profile, hamburger)
 * - Floating popovers anchored to each button
 * - Share sheet uses the unified ShareMenu primitive
 *
 * All hover/focus states come from IconButton — no inline mouseEnter handlers.
 */
function useOutsideClose(ref, isOpen, onClose) {
    useEffect(() => {
        if (!isOpen) return;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [ref, isOpen, onClose]);
}

function Popover({ open, children, className }) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
                    className={clsx(
                        'absolute end-0 top-full mt-2 min-w-[14rem]',
                        'bg-surface rounded-xl border border-line-subtle shadow-elevated',
                        'p-1.5 z-modal',
                        className,
                    )}
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function PopoverItem({ icon: Icon, children, onClick, tone = 'neutral' }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={clsx(
                'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold text-right',
                'transition-colors duration-150',
                'hover:bg-surface-muted active:bg-surface-sunken',
                'focus:outline-none focus-visible:bg-surface-muted',
                tone === 'danger' ? 'text-danger-700 hover:bg-danger-50' : 'text-ink',
            )}
        >
            {Icon && <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />}
            <span className="flex-1 text-right">{children}</span>
        </button>
    );
}

export function Navbar({ chatId }) {
    const [showSearch, setShowSearch] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showShare, setShowShare] = useState(false);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [originalResults, setOriginalResults] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);

    const searchRef = useRef(null);
    const userMenuRef = useRef(null);
    const menuRef = useRef(null);

    useOutsideClose(searchRef, showSearch, () => { setShowSearch(false); setQuery(''); setResults([]); });
    useOutsideClose(userMenuRef, showUserMenu, () => setShowUserMenu(false));
    useOutsideClose(menuRef, showMenu, () => setShowMenu(false));

    // Search logic — preserved from previous Navbar
    useEffect(() => {
        if (query.length < 3) {
            setResults([]);
            if (hasFetched) { setOriginalResults([]); setHasFetched(false); }
            return;
        }
        if (originalResults.length > 0) return;
        const t = setTimeout(async () => {
            try {
                const res = await safeFetch(
                    `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/search-businesses?query=${encodeURIComponent(query)}`,
                    chatId,
                );
                const data = await res.json();
                setOriginalResults(data || []);
                setHasFetched(true);
                const filtered = (data || []).filter(item =>
                    (item.business_name || '').toLowerCase().includes(query.toLowerCase()) ||
                    (item.business_description || '').toLowerCase().includes(query.toLowerCase())
                );
                setResults(filtered);
            } catch (err) {
                console.error('Search error:', err);
                setOriginalResults([]); setResults([]); setHasFetched(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [query, originalResults.length, hasFetched, chatId]);

    useEffect(() => {
        if (query.length < 3) return;
        setResults(originalResults.filter(item =>
            item.business_name?.toLowerCase().includes(query.toLowerCase()) ||
            item.business_description?.toLowerCase().includes(query.toLowerCase())
        ));
    }, [query, originalResults]);

    // Share URL — adapt to current page if it's a store page
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    const paramSlug = urlParams.get('slug');
    const isStorePath = pathname && pathname !== '/' && !urlParams.get('view');
    const storeSlug = paramSlug || (isStorePath ? pathname.substring(1) : null);
    const shareUrl = storeSlug ? `https://hamekomit.com/${encodeURIComponent(storeSlug)}` : 'https://hamekomit.com';

    const isLoggedIn = chatId && !(typeof chatId === 'string' && chatId.startsWith('guest'));

    const handleLogout = () => {
        document.cookie = 'mekomitChatId=; path=/; max-age=0';
        sessionStorage.setItem('redirectAfterLogin', window.location.search);
        localStorage.removeItem('chat_id');
        navigate('zones');
        window.location.reload();
    };

    return (
        <>
            <motion.header
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                className="sticky top-0 z-modal-back bg-surface/85 backdrop-blur-md backdrop-saturate-150 border-b border-line-subtle"
            >
                <div className="app-container">
                    <div className="flex items-center justify-end h-14 select-none gap-1">
                        {/* Search */}
                        <div ref={searchRef} className="relative">
                            <IconButton
                                icon={<Search />}
                                aria-label="חיפוש"
                                variant="ghost"
                                tone="brand"
                                shape="round"
                                onClick={() => setShowSearch(p => !p)}
                            />
                            <Popover open={showSearch} className="w-80">
                                <div className="p-2">
                                    <div className="relative">
                                        <Search className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                                        <input
                                            type="text"
                                            placeholder="חיפוש עסקים..."
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            autoFocus
                                            dir="rtl"
                                            className={clsx(
                                                'w-full bg-surface-muted text-ink text-sm rounded-lg ps-9 pe-3 py-2.5',
                                                'border border-line-subtle',
                                                'placeholder:text-ink-faint',
                                                'transition-[border-color,background-color,box-shadow] duration-200',
                                                'focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/25 focus:bg-surface',
                                            )}
                                        />
                                    </div>
                                    {query.length < 3 && (
                                        <p className="text-xs text-ink-subtle px-2 pt-3 pb-1 text-right">הקלידו לפחות 3 תווים</p>
                                    )}
                                    {query.length >= 3 && results.length === 0 && (
                                        <p className="text-xs text-ink-subtle px-2 py-4 text-center">אין תוצאות עבור "{query}"</p>
                                    )}
                                    {query.length >= 3 && results.length > 0 && (
                                        <ul className="mt-2 max-h-64 overflow-y-auto space-y-0.5">
                                            {results.map(biz => (
                                                <li key={biz.business_id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            navigate('business', `entity=${biz.business_id}`);
                                                            setShowSearch(false);
                                                        }}
                                                        className="w-full px-3 py-2.5 text-sm font-medium text-right rounded-lg hover:bg-surface-muted active:bg-surface-sunken text-ink transition-colors"
                                                    >
                                                        {biz.business_name}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </Popover>
                        </div>

                        {/* User menu */}
                        <div ref={userMenuRef} className="relative">
                            <IconButton
                                icon={<User />}
                                aria-label="פרופיל"
                                variant="ghost"
                                tone="brand"
                                shape="round"
                                onClick={() => setShowUserMenu(p => !p)}
                            />
                            <Popover open={showUserMenu}>
                                {isLoggedIn ? (
                                    <PopoverItem icon={LogOut} tone="danger" onClick={handleLogout}>התנתק</PopoverItem>
                                ) : (
                                    <PopoverItem icon={LogIn} onClick={handleLogout}>התחבר</PopoverItem>
                                )}
                            </Popover>
                        </div>

                        {/* Hamburger menu */}
                        <div ref={menuRef} className="relative">
                            <IconButton
                                icon={<Menu />}
                                aria-label="תפריט"
                                variant="ghost"
                                tone="brand"
                                shape="round"
                                onClick={() => setShowMenu(p => !p)}
                            />
                            <Popover open={showMenu}>
                                <PopoverItem icon={Info} onClick={() => { setShowMenu(false); navigate('about'); }}>אודות</PopoverItem>
                                <PopoverItem icon={MessageCircle} onClick={() => { setShowMenu(false); navigate('contact'); }}>צור קשר</PopoverItem>
                                <PopoverItem icon={Share2} onClick={() => { setShowMenu(false); setShowShare(true); }}>שתף</PopoverItem>
                            </Popover>
                        </div>
                    </div>
                </div>
            </motion.header>

            <ShareMenu
                isOpen={showShare}
                onClose={() => setShowShare(false)}
                url={shareUrl}
                title="בואו לבקר במקומית"
            />
        </>
    );
}

export default Navbar;
